/**
 * Admin Portal Authentication Types
 * 
 * Simplified types for the admin portal (subset of shared types)
 */

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  avatarFilename?: string;
  avatarUrl?: string;
  role: UserRole;
  companyId?: string;
  permissions: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type UserRole = 'CONSUMER' | 'COMPANY_USER' | 'COMPANY_ADMIN' | 'PLATFORM_ADMIN';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
  permissions: string[];
}

export interface LoginRequest {
  email: string;
  password: string;
  userType?: string;
  rememberMe?: boolean;
}

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  tokens: AuthTokens | null;
  permissions: string[];
  error: string | null;
}

export interface AuthContextType {
  // State
  authState: AuthState;
  
  // Actions
  login: (credentials: LoginRequest) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  
  // Utilities
  hasPermission: (permission: string) => boolean;
  hasRole: (role: UserRole) => boolean;
  isPlatformAdmin: () => boolean;
}

// Role constants
export const ROLES = {
  CONSUMER: 'CONSUMER' as const,
  COMPANY_USER: 'COMPANY_USER' as const,
  COMPANY_ADMIN: 'COMPANY_ADMIN' as const,
  PLATFORM_ADMIN: 'PLATFORM_ADMIN' as const,
} as const;

// Permission constants (subset for admin)
export const PERMISSIONS = {
  // Platform admin permissions
  PLATFORM_COMPANIES_READ: 'platform:companies:read',
  PLATFORM_COMPANIES_CREATE: 'platform:companies:create',
  PLATFORM_COMPANIES_UPDATE: 'platform:companies:update',
  PLATFORM_USERS_READ: 'platform:users:read',
  PLATFORM_USERS_UPDATE: 'platform:users:update',
  PLATFORM_USERS_DELETE: 'platform:users:delete',
  PLATFORM_ANALYTICS_READ: 'platform:analytics:read',
  PLATFORM_SETTINGS_UPDATE: 'platform:settings:update',
} as const;